import React, { useEffect, useState, createRef, useContext } from "react";

import MeasurementsAPI from "../../API/measurements.api";
import ScansAPI from "../../API/scans.api";
import LookupsAPI from "../../API/lookups.api";
import UserContext from "../../lib/UserContext";
import { ErrorAlert } from "../../utils/Alerts";
import SwitchButton from "../Buttons/SwitchButton";
import { Collapse } from "react-collapse";
import {
    AutoSizer,
    List,
    CellMeasurer,
    CellMeasurerCache,
} from "react-virtualized";
import LoadingOverlay from "../Loaders/LoadingOverlay";
import FilterDropdown from "../Dropdown/FilterDropdown";
import NoDataView from "../Shared/NoDataView";
import { Trans, useTranslation } from "react-i18next";
import { getMappedFilters } from "../../utils/FilterHelpers";
import BaseModal from "./BaseModal";

const cache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 100,
});

const CloudUploadModal = ({
    isVisible,
    setIsVisible,
    applyCloudData,
    scansFilterOptions,
    measurmentsFilterOptions,
    scanAppliedFilters,
    measurementAppliedFilters,
    setScanAppliedFilters,
    setMeasurementAppliedFilters,
    selectedMaterialName,
}) => {
    const getMaterialNameFilter = () =>
        selectedMaterialName ? { materialName: selectedMaterialName } : {};

    const [isLoading, setIsLoading] = useState(false);
    const [mainCollapse, setMainCollapse] = useState({});
    const [innerCollapse, setInnerCollapse] = useState({});

    const [measurementsCollapse, setMeasurementsCollapse] = useState({});
    const [measurementsInnerCollapse, setMeasurementsInnerCollapse] = useState(
        {}
    );
    const [scansCollapse, setScansCollapse] = useState({});
    const [scansInnerCollapse, setScansInnerCollapse] = useState({});

    const [selectedRows, setSelectedRows] = useState({});

    const [data, setData] = useState([]);
    const [measurements, setMeasurements] = useState([]);
    const [scans, setScans] = useState([]);

    const [filters, setFilters] = useState(() => getMaterialNameFilter());
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getMaterialNameFilter()
    );

    const [measurementFilters, setMeasurementFilters] = useState(() =>
        getMaterialNameFilter()
    );

    const [scanFilters, setScanFilters] = useState(() =>
        getMaterialNameFilter()
    );

    const [currentUser, setCurrentUser] = useState({});
    const [isCollect, setIsCollect] = useState(true);
    const list = createRef(null);

    const context = useContext(UserContext);
    const { t } = useTranslation();

    useEffect(() => {
        setIsLoading(true);
        setCurrentUser(context.getUser());
        fetchMaterialsMeasurements(measurementFilters).then((result) => {
            setData(result);
            setMeasurements(result);
        });
        fetchMaterialsScans(scanFilters).then((result) => {
            setScans(result);
            setIsLoading(false);
        });
    }, []);

    useEffect(() => {
        cache.clearAll();
        list.current?.recomputeRowHeights();
    }, [data]);

    useEffect(() => {
        if (isCollect) {
            setData(measurements);
            setScansCollapse({ ...mainCollapse });
            setScansInnerCollapse({ ...innerCollapse });

            setMainCollapse({ ...measurementsCollapse });
            setInnerCollapse({ ...measurementsInnerCollapse });

            setFilters({ ...measurementFilters });
            setAppliedFilters({ ...measurementFilters });
        } else {
            setData(scans);
            setMeasurementsCollapse({ ...mainCollapse });
            setMeasurementsInnerCollapse({ ...innerCollapse });

            setMainCollapse({ ...scansCollapse });
            setInnerCollapse({ ...scansInnerCollapse });

            setFilters({ ...scanFilters });
            setAppliedFilters({ ...scanFilters });
        }
    }, [isCollect]);

    const fetchMaterialsMeasurements = async (filters = null) => {
        const newFilters = getMappedFilters(filters);
        const result = await LookupsAPI.getMaterialsMeasurements(newFilters);
        return result.map((material) => {
            return { ...material, value: null };
        });
    };

    const fetchMaterialsScans = async (filters = null) => {
        const newFilters = getMappedFilters(filters);
        const result = await LookupsAPI.getMaterialsScans(newFilters);
        return result.map((material) => {
            return { ...material, value: null };
        });
    };

    const fetchMeasurementData = async (filterData = null) => {
        const newFilters = getMappedFilters({
            ...filterData,
            ...measurementAppliedFilters,
        });
        const result = await MeasurementsAPI.getMeasurements({
            ...newFilters,
        });
        const mappedResult = measurements.map((measurement) => {
            if (measurement?.id === result[0]?.id)
                return { ...measurement, value: result[0].value };
            return measurement;
        });
        setMeasurements(mappedResult);
        if (isCollect) setData(mappedResult || []);
    };

    const fetchScanData = async (filterData = null) => {
        const newFilters = getMappedFilters({
            ...filterData,
            ...scanAppliedFilters,
        });
        const result = await ScansAPI.getScansList({
            ...newFilters,
        });
        const mappedResult = scans.map((scan) => {
            if (scan?.id === result[0]?.id)
                return { ...scan, value: result[0].value };
            return scan;
        });
        setScans(mappedResult);
        if (!isCollect) setData(mappedResult || []);
    };

    const _getData = (index) => {
        return data[index];
    };

    const closeModal = () => {
        setIsVisible(false);
    };

    const getFilterChanges = () => {
        let _scanFilters = { ...scanAppliedFilters };
        let _measurementFilters = { ...measurementAppliedFilters };

        if (isCollect) {
            _measurementFilters = { ..._measurementFilters, ...appliedFilters };
        } else {
            _scanFilters = { ..._scanFilters, ...appliedFilters };
        }

        return { _scanFilters, _measurementFilters };
    };

    const submitData = () => {
        let scansCriteria = [];
        let measurementCriteria = [];
        const _filters = getFilterChanges();
        Object.values(selectedRows).forEach((row) => {
            const criteria = {
                materialId: row.materialId,
                userId: row.userId,
                numberOfPoints: row.numberOfPoints,
            };
            if (row.isCollect) {
                measurementCriteria.push(criteria);
            } else {
                scansCriteria.push(criteria);
            }
        });

        closeModal();
        applyCloudData(measurementCriteria, scansCriteria, _filters);
    };

    const _noRowsRenderer = () => {
        return (
            <NoDataView entity={t("measurements")} includeText={!isLoading} />
        );
    };

    const handleSampleChecked = (
        key,
        index,
        innerIndex,
        numberOfPoints,
        userId
    ) => {
        const collectOrScan = isCollect ? "collect" : "scan";
        let newSelected = { ...selectedRows };
        const selectedValues = Object.values(newSelected);
        if (
            selectedValues.length > 0 &&
            selectedValues[0].numberOfPoints !== numberOfPoints
        ) {
            ErrorAlert(t("selectDifferentPointsMessage"));
            return;
        }

        const isSelected =
            newSelected[`${collectOrScan}-${key}-${index}-${innerIndex}`];
        if (!isSelected) {
            newSelected[`${collectOrScan}-${key}-${index}-${innerIndex}`] = {
                materialId: key,
                index,
                numberOfPoints,
                userId,
                innerIndex,
                isCollect,
            };
        } else {
            delete newSelected[
                `${collectOrScan}-${key}-${index}-${innerIndex}`
            ];
        }

        setSelectedRows(newSelected);
    };

    const handleExpand = (index, innerData) => {
        let opened = { ...mainCollapse };
        opened[index] = !mainCollapse[index];
        if (opened[index] && !innerData.value) {
            setIsLoading(true);
            if (isCollect) {
                fetchMeasurementData({
                    materialId: innerData.id,
                }).then(() => {
                    setIsLoading(false);
                });
            } else {
                fetchScanData({
                    materialId: innerData.id,
                }).then(() => {
                    setIsLoading(false);
                });
            }
        }
        setMainCollapse(opened);

        cache.clear(index);

        // set innercells
        let collapse = { ...innerCollapse };
        if (!collapse[index]) {
            collapse[index] = {};
            setInnerCollapse(collapse);
        }
    };

    const handleInnerExpand = (index, innerIndex) => {
        let collapse = { ...innerCollapse };
        const isCollapse = collapse[index];
        isCollapse[innerIndex] = isCollapse ? !isCollapse[innerIndex] : true;

        setInnerCollapse(collapse);
        cache.clear(index);
    };

    const getCollapseValue = (index, innerIndex) => {
        const isCollapse = innerCollapse[index];
        return isCollapse ? isCollapse[innerIndex] : false;
    };

    const handleRefreshClicked = () => {
        setMainCollapse({});
        setInnerCollapse({});

        setScansCollapse({});
        setScansInnerCollapse({});

        setMeasurementsCollapse({});
        setMeasurementsInnerCollapse({});

        setMeasurementFilters({});
        setMeasurementAppliedFilters({});
        setScanFilters({});
        setScanAppliedFilters({});

        setIsLoading(true);
        fetchMaterialsMeasurements(filters)
            .then((measurementsResult) => {
                if (isCollect) setData(measurementsResult);
                setMeasurements(measurementsResult);
                fetchMaterialsScans().then((scansResult) => {
                    setScans(scansResult);
                    if (!isCollect) setData(scansResult);
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleChecked = (key, index, innerIndex) => {
        const collectOrScan = isCollect ? "collect" : "scan";
        return selectedRows[`${collectOrScan}-${key}-${index}-${innerIndex}`]
            ? true
            : false;
    };

    const innerRowRenderer = (innerData, index) => {
        if (innerData.value) {
            return (
                <Collapse isOpened={mainCollapse[index]}>
                    <hr />
                    {innerData.value.map((item, itemIndex) => {
                        const isCollapsed = getCollapseValue(index, itemIndex);
                        const isLastIndex =
                            itemIndex === innerData.value.length - 1;
                        return (
                            <div
                                key={`list-${itemIndex}`}
                                className="dataset-items-body"
                            >
                                <br />
                                <div
                                    className={`dataset-content px-10 py-1 cursor-pointer ${
                                        isLastIndex ? "mb-4" : ""
                                    }`}
                                    onClick={() =>
                                        handleInnerExpand(index, itemIndex)
                                    }
                                >
                                    <i
                                        className={`mr-2 fas fa-caret-${
                                            isCollapsed ? "down" : "right"
                                        }`}
                                    />
                                    {item.email}
                                    {item.email === currentUser.email
                                        ? t("me")
                                        : ""}
                                </div>
                                {pointsRowRenderer(
                                    isCollapsed,
                                    innerData.id,
                                    index,
                                    item,
                                    itemIndex
                                )}
                            </div>
                        );
                    })}
                </Collapse>
            );
        }
    };

    const pointsRowRenderer = (isCollapsed, id, index, item, itemIndex) => {
        return (
            <Collapse isOpened={isCollapsed}>
                <div className="dataset-sub-content px-14 mt-3 mb-3">
                    <div className="dataset-items-sub-level-body mb-3">
                        <div className="grid grid-cols-2 gap-4 mt-1 px-4 uppercase dataset-items-header dataset-btn">
                            <label>
                                <Trans i18nKey="numberOfPoints">
                                    Number of Points
                                </Trans>
                            </label>
                            <label className="text-center">
                                <Trans i18nKey="numberOfMeasurements">
                                    Number of Measurements
                                </Trans>
                            </label>
                        </div>
                        <hr />
                        {item.points.map((point, subIndex) => {
                            return (
                                <div
                                    key={`sublist-${subIndex}`}
                                    className="grid grid-cols-2 gap-4 mt-4 ml-4"
                                >
                                    <div className="checkbox-wrapper-input filter-checker-btn">
                                        <input
                                            type="checkbox"
                                            className="checkbox-element"
                                            id={`checkbox-ele-${index}-${itemIndex}-${subIndex}-point`}
                                            checked={handleChecked(
                                                id,
                                                itemIndex,
                                                subIndex
                                            )}
                                            onChange={() =>
                                                handleSampleChecked(
                                                    id,
                                                    itemIndex,
                                                    subIndex,
                                                    point.numberOfPoints,
                                                    item.id
                                                )
                                            }
                                        />
                                        <label
                                            className="black"
                                            htmlFor={`checkbox-ele-${index}-${itemIndex}-${subIndex}-point`}
                                        >
                                            <b className="mr-1">
                                                {point.numberOfPoints}
                                            </b>
                                            <Trans i18nKey="pointsDataset">
                                                Points Dataset
                                            </Trans>
                                        </label>
                                    </div>
                                    <label className="text-center">
                                        {point.count}
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Collapse>
        );
    };

    const _rowRenderer = ({ index, key, style, parent }) => {
        const retrievedData = _getData(index);
        return (
            <CellMeasurer
                key={key}
                cache={cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {({ registerChild }) => (
                    <div
                        ref={registerChild}
                        className="dataset-items border-bottom"
                        style={style}
                        key={`row-${key}`}
                    >
                        <div
                            onClick={() => handleExpand(index, retrievedData)}
                            className="dataset-items-header dataset-btn py-2 px-5 cursor-pointer"
                        >
                            <i
                                className={`fas fa-caret-${
                                    mainCollapse[index] ? "down" : "right"
                                } mr-2`}
                            />
                            {retrievedData.name}
                        </div>
                        {innerRowRenderer(retrievedData, index)}
                    </div>
                )}
            </CellMeasurer>
        );
    };

    const applyFilters = (filterData) => {
        setIsLoading(true);

        if (isCollect) {
            fetchMaterialsMeasurements(filterData)
                .then((result) => {
                    setData(result);
                    setMeasurements(result);
                    setMeasurementFilters(filterData);
                    setMeasurementAppliedFilters(filterData);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            fetchMaterialsScans(filterData)
                .then((result) => {
                    setData(result);
                    setScans(result);
                    setScanFilters(filterData);
                    setScanAppliedFilters(filterData);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                title={t("chooseDatasets")}
                titleIcon="fas fa-tasks"
                action={{
                    name: t("retrieve"),
                    submitAction: submitData,
                    disabled: !Object.keys(selectedRows)?.length,
                }}
            >
                <div className="flex justify-between mb-2">
                    <SwitchButton
                        firstButton={t("collect")}
                        secondButton={t("scan")}
                        isActive={isCollect}
                        setIsActive={setIsCollect}
                    />
                    <div className="flex items-center">
                        <FilterDropdown
                            filterOptions={
                                isCollect
                                    ? measurmentsFilterOptions
                                    : scansFilterOptions
                            }
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component={
                                isCollect
                                    ? "measurement-cloud-modal"
                                    : "scan-cloud-modal"
                            }
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            disabled={isLoading}
                        />
                        <button
                            className="secondary-green-btn flex items-center ml-2"
                            onClick={handleRefreshClicked}
                            disabled={isLoading}
                            id="refresh-btn"
                        >
                            <i className="fas fa-redo mr-2" />
                            <Trans i18nKey="refresh">Refresh</Trans>
                        </button>
                    </div>
                </div>
                <LoadingOverlay active={isLoading} text="Loading...">
                    <div className="dataset-wrapper ag-grid-height">
                        <AutoSizer>
                            {({ width, height }) => (
                                <List
                                    className="List"
                                    height={height}
                                    noRowsRenderer={_noRowsRenderer}
                                    rowCount={data.length}
                                    rowHeight={cache.rowHeight}
                                    rowRenderer={_rowRenderer}
                                    deferredMeasurementCache={cache}
                                    width={width}
                                    ref={list}
                                />
                            )}
                        </AutoSizer>
                    </div>
                </LoadingOverlay>
            </BaseModal>
        </>
    );
};

export default CloudUploadModal;
